import { TransactionDirectionType } from "@common/categories";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import React, { useMemo, useRef } from "react";
import { useCategoryLabelGetter } from "@hooks/useCategoryLabelGetter";
import { useIdCopy } from "@hooks/useIdCopy";
import { CategoryIcon } from "./CategoryIcon";
import "./TransactionCategory.scss";

interface Props {
    transaction: TransactionListItem;
}

export const TransactionCategory: React.FC<Props> = React.memo(
    ({ transaction }) => {
        const getCategoryLabel = useCategoryLabelGetter();
        const { getCopyProps } = useIdCopy({ label: "Category" });
        const labelRef = useRef<HTMLSpanElement>(null);
        const truncationTooltipProps = useTruncationTooltip({
            ref: labelRef,
            isTruncated: isOverflowingX,
        });

        const transactionDirection =
            transaction.amount > 0
                ? TransactionDirectionType.incoming
                : TransactionDirectionType.outgoing;

        const label = useMemo(() => {
            if (!transaction.category) {
                return null;
            }
            return getCategoryLabel(
                transaction.category.id,
                transactionDirection,
            );
        }, [getCategoryLabel, transaction.category, transactionDirection]);

        if (!transaction.category) {
            return null;
        }

        return (
            <Tooltip content={label} {...truncationTooltipProps}>
                <span
                    className="transaction-category"
                    data-testid="transaction-category"
                    {...getCopyProps(transaction.categoryId)}
                >
                    <CategoryIcon
                        categoryId={transaction.categoryId}
                        transactionDirection={transactionDirection}
                    />
                    <span
                        ref={labelRef}
                        className="transaction-category__label"
                    >
                        {label}
                    </span>
                </span>
            </Tooltip>
        );
    },
);
TransactionCategory.displayName = "TransactionCategory";
